import React, { Suspense, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ToastProvider } from './contexts/ToastContext';
import { ThemeProvider } from './contexts/ThemeContext';
import RequireAuth from './components/RequireAuth';
import RequirePermission from './components/RequirePermission';
import SessionLoader from './components/SessionLoader';
import { useAuth } from './hooks/useAuth';
import { lazyWithRetry } from './utils/lazyWithRetry';
import './App.css';

// Screens are loaded per route so the initial bundle stays small — heavy deps
// (three, recharts, spline) only ship with the screen that needs them.
const Dashboard = lazyWithRetry(() => import('./screens/Dashboard'));
const ProjectSetup = lazyWithRetry(() => import('./screens/ProjectSetup'));
const AgentList = lazyWithRetry(() => import('./screens/AgentList'));
const AgentDetails = lazyWithRetry(() => import('./screens/AgentDetails'));
const AgentTraining = lazyWithRetry(() => import('./screens/AgentTraining'));
const ChannelConfig = lazyWithRetry(() => import('./screens/ChannelConfig'));
const ChatPreview = lazyWithRetry(() => import('./screens/ChatPreview'));
const ConversationHistory = lazyWithRetry(() => import('./screens/ConversationHistory'));
const AgentActions = lazyWithRetry(() => import('./screens/AgentActions'));
const VoiceAgent = lazyWithRetry(() => import('./screens/VoiceAgent'));
const WidgetTest = lazyWithRetry(() => import('./screens/WidgetTest'));
const AgentAutomations = lazyWithRetry(() => import('./screens/AgentAutomations'));
const Users = lazyWithRetry(() => import('./screens/Users'));
const ChangePassword = lazyWithRetry(() => import('./screens/ChangePassword'));
// HIDDEN: Security, Billing and Tools screens are temporarily disabled.
const Escalations = lazyWithRetry(() => import('./screens/Escalations'));
const Leads = lazyWithRetry(() => import('./screens/Leads'));
const Profile = lazyWithRetry(() => import('./screens/Profile'));
const Logs = lazyWithRetry(() => import('./screens/Logs'));
const Login = lazyWithRetry(() => import('./screens/Login'));
const Register = lazyWithRetry(() => import('./screens/Register'));
const LandingPage = lazyWithRetry(() => import('./screens/LandingPage'));
const FeaturesPage = lazyWithRetry(() => import('./screens/FeaturesPage'));
const PricingPage = lazyWithRetry(() => import('./screens/PricingPage'));
const AboutPage = lazyWithRetry(() => import('./screens/AboutPage'));
const ContactPage = lazyWithRetry(() => import('./screens/ContactPage'));
const PublicLeadForm = lazyWithRetry(() => import('./screens/PublicLeadForm'));

// Component to initialize auth inside Router context
function AuthInitializer() {
  const { initialiseAuth } = useAuth();
  const hasInitialized = useRef(false);

  useEffect(() => {
    // Guard against double invocation (e.g., from StrictMode)
    if (hasInitialized.current) return;
    hasInitialized.current = true;
    initialiseAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount
  return null;
}

/**
 * Copy for the screens scoped out of the lead-desk roles (LEAD_MANAGER and
 * MEMBER) and the demo CUSTOMER role. RequirePermission's default line ("limited to organization owners
 * and admins") is technically true for most of these, but it names a rule
 * rather than the reason — the screen isn't withheld as a privilege, it's
 * outside the role's workspace.
 *
 * Deliberately doesn't spell out what the workspace *does* cover: the two
 * lead-desk roles have different surfaces (LEAD_MANAGER sees leads alone,
 * MEMBER also gets the dashboard), so any enumeration here would be wrong for
 * one of them. RequirePermission already appends the reader's own role.
 */
const leadDeskReason = (title: string) =>
  `${title} is not part of your workspace.`;

function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <Router>
          <AuthInitializer />
          <div className="App">
            <Suspense fallback={<SessionLoader />}>
              <Routes>
                {/* Marketing Pages (Public) */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/features" element={<FeaturesPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/lead-form/:token" element={<PublicLeadForm />} />

                {/* Auth Pages */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />

                {/* Widget Test Page (Public — no login, mirrors a real embed) */}
                <Route path="/widget-test" element={<WidgetTest />} />
                <Route path="/widget-test/:widgetKey" element={<WidgetTest />} />

                {/* Forced password change (Protected, but reachable while
                    mustChangePassword is still set — every other protected
                    route redirects here until it's cleared) */}
                <Route
                  path="/change-password"
                  element={
                    <RequireAuth allowPasswordChange>
                      <ChangePassword />
                    </RequireAuth>
                  }
                />

                {/* Dashboard Routes (Protected). Both paths render the same
                    screen, so both carry the same gate — LEAD_MANAGER is scoped
                    to Leads alone and has no dashboard nav entry to reach it
                    with; this catches a typed URL or a stale bookmark. */}
                <Route
                  path="/dashboard"
                  element={
                    <RequireAuth>
                      <RequirePermission
                        permission="dashboard.view"
                        activeSection="dashboard"
                        title="Dashboard"
                        reason={leadDeskReason('The dashboard')}
                      >
                        <Dashboard />
                      </RequirePermission>
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workspaces"
                  element={
                    <RequireAuth>
                      <RequirePermission
                        permission="dashboard.view"
                        activeSection="dashboard"
                        title="Dashboard"
                        reason={leadDeskReason('The dashboard')}
                      >
                        <Dashboard />
                      </RequirePermission>
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workspaces/:projectId"
                  element={
                    <RequireAuth>
                      <RequirePermission
                        permission="workspaceSettings.access"
                        activeSection="settings"
                        title="Settings"
                        reason={leadDeskReason('Settings')}
                      >
                        <ProjectSetup />
                      </RequirePermission>
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workspaces/:projectId/agents"
                  element={
                    <RequireAuth>
                      <RequirePermission
                        permission="agents.view"
                        activeSection="agents"
                        title="Agent (AI Builder)"
                        reason={leadDeskReason('Agent (AI Builder)')}
                      >
                        <AgentList />
                      </RequirePermission>
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workspaces/:projectId/agents/:agentId"
                  element={
                    <RequireAuth>
                      <RequirePermission
                        permission="agents.view"
                        activeSection="agents"
                        title="Agent (AI Builder)"
                        reason={leadDeskReason('Agent (AI Builder)')}
                      >
                        <AgentDetails />
                      </RequirePermission>
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workspaces/:projectId/agents/:agentId/training"
                  element={
                    <RequireAuth>
                      <RequirePermission
                        permission="agents.view"
                        activeSection="knowledge"
                        title="Agent (AI Builder)"
                        reason={leadDeskReason('Agent (AI Builder)')}
                      >
                        <AgentTraining />
                      </RequirePermission>
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workspaces/:projectId/agents/:agentId/channels"
                  element={
                    <RequireAuth>
                      <RequirePermission
                        permission="channels.manage"
                        activeSection="channels"
                        title="Channels"
                      >
                        <ChannelConfig />
                      </RequirePermission>
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workspaces/:projectId/agents/:agentId/widget"
                  element={
                    <RequireAuth>
                      <RequirePermission
                        permission="channels.manage"
                        activeSection="channels"
                        title="Channels"
                      >
                        <ChannelConfig />
                      </RequirePermission>
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workspaces/:projectId/agents/:agentId/chat-preview"
                  element={
                    <RequireAuth>
                      <RequirePermission
                        permission="agents.view"
                        activeSection="agents"
                        title="Chat Preview"
                        reason={leadDeskReason('Chat Preview')}
                      >
                        <ChatPreview />
                      </RequirePermission>
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workspaces/:projectId/agents/:agentId/conversations"
                  element={
                    <RequireAuth>
                      <RequirePermission
                        permission="conversations.view"
                        activeSection="conversations"
                        title="Conversations"
                        reason={leadDeskReason('Conversations')}
                      >
                        <ConversationHistory />
                      </RequirePermission>
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workspaces/:projectId/agents/:agentId/actions"
                  element={
                    <RequireAuth>
                      <RequirePermission
                        permission="actions.manage"
                        activeSection="agents"
                        title="Actions"
                      >
                        <AgentActions />
                      </RequirePermission>
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workspaces/:projectId/agents/:agentId/voice-agent"
                  element={
                    <RequireAuth>
                      <RequirePermission
                        permission="voice.access"
                        activeSection="voiceAgent"
                        title="Voice Agent"
                        reason={leadDeskReason('Voice Agent')}
                      >
                        <VoiceAgent />
                      </RequirePermission>
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workspaces/:projectId/agents/:agentId/automations"
                  element={
                    <RequireAuth>
                      <RequirePermission
                        permission="automations.access"
                        activeSection="channels"
                        title="Automations"
                      >
                        <AgentAutomations />
                      </RequirePermission>
                    </RequireAuth>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <RequireAuth>
                      <RequirePermission
                        permission="users.view"
                        activeSection="users"
                        title="Users"
                        reason={leadDeskReason('Users')}
                      >
                        <Users />
                      </RequirePermission>
                    </RequireAuth>
                  }
                />
                {/* HIDDEN: /security, /billing, /tools routes temporarily disabled */}
                <Route
                  path="/escalations"
                  element={
                    <RequireAuth>
                      <RequirePermission
                        permission="escalations.access"
                        activeSection="escalations"
                        title="Support Tickets"
                        reason={leadDeskReason('Support Tickets')}
                      >
                        <Escalations />
                      </RequirePermission>
                    </RequireAuth>
                  }
                />
                <Route
                  path="/leads"
                  element={
                    <RequireAuth>
                      <RequirePermission
                        permission="leads.view"
                        activeSection="leads"
                        title="Leads"
                        reason={leadDeskReason('Leads')}
                      >
                        <Leads />
                      </RequirePermission>
                    </RequireAuth>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <RequireAuth>
                      <Profile />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/logs"
                  element={
                    <RequireAuth>
                      <RequirePermission
                        permission="logs.access"
                        activeSection="logs"
                        title="Logs"
                      >
                        <Logs />
                      </RequirePermission>
                    </RequireAuth>
                  }
                />
              </Routes>
            </Suspense>
          </div>
        </Router>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
